import type { EchoflicksSessionEvent } from "../types";

type JsonRecord = Record<string, unknown>;

export type ContinuitySeamAudit = {
  seamId: string;
  boundary: string;
  strategy: string;
  execution: string;
  confidence?: number;
  identityRisk?: string;
  reasons: string[];
};

export type ContinuityAttemptAudit = {
  attemptId: string;
  planId?: string;
  seamId: string;
  sceneId?: number;
  attempt?: number;
  status?: string;
  provider?: string;
  concreteProvider?: string;
  model?: string;
  elapsedMs?: number;
  fallback: boolean;
  errorCode?: string;
  legId?: string;
  legDirection?: string;
  endFrame?: {
    requested: boolean;
    applied: boolean;
    hashAgreement?: boolean;
    role?: string;
  };
};

export type FusionPersonSafetyAudit = {
  state?: string;
  policyVersion?: string;
  detectorVersions: string[];
  sampledFrameCount?: number;
  positiveFrameCount?: number;
  maxScore?: number;
  primaryConfidence?: number;
  reasonCode?: string;
};

export type FinalContentSelectionAudit = {
  sceneId?: number;
  assetLocatorSha256?: string;
  assetProvenance?: string;
  providerId?: string;
  fallbackReason?: string;
  continuityBridgeId?: string;
  continuityLegId?: string;
  continuityDisposition?: string;
};

export type ContinuityQcAudit = {
  seamId: string;
  sceneId?: number;
  attempt?: number;
  provider?: string;
  disposition?: string;
  issueCodes: string[];
  recommendedFallback?: string;
};

export type ContinuityCostAudit = {
  estimatedCostUsd?: string;
  providerReportedCostUsd?: string;
  attemptCount: number;
  billableAttemptCount: number;
  fallbackAttemptCount: number;
  qcRejectedAttemptCount: number;
};

export type ContinuityAudit = {
  evaluated?: {
    enabled: boolean;
    productFlow?: string;
    reason?: string;
    configReleaseId?: string;
    configDigest?: string;
  };
  plan?: {
    planId?: string;
    policyVersion?: string;
    seams: ContinuitySeamAudit[];
  };
  validation?: {
    valid: boolean;
    reason?: string;
    planId?: string;
  };
  degradations: Array<{
    seamId?: string;
    sceneId?: number;
    reason?: string;
    fallback?: string;
  }>;
  attempts: ContinuityAttemptAudit[];
  qc: ContinuityQcAudit[];
  cost?: ContinuityCostAudit;
  fusion?: {
    bridgeId: string;
    seamId?: string;
    strategy?: string;
    outgoingLegId?: string;
    incomingLegId?: string;
    joinMethod?: string;
    candidateCount?: number;
    acceptedCandidateCount?: number;
    executionStatus?: string;
    qcDisposition?: string;
    qcIssueCodes: string[];
    erasureStatus?: string;
    deletedObjectCount?: number;
    committed?: boolean;
    fallbackRegenerated?: boolean;
    personSafety?: {
      outgoing?: FusionPersonSafetyAudit;
      incoming?: FusionPersonSafetyAudit;
    };
  };
  configReadback?: {
    desiredReleaseId?: string;
    publishedReleaseId?: string;
    appliedReleaseId?: string;
    frozenReleaseId?: string;
    desiredDigest?: string;
    publishedDigest?: string;
    appliedDigest?: string;
    frozenDigest?: string;
    service?: string;
    serviceReleaseSha?: string;
    validationResult?: string;
  };
  finalContent?: {
    schemaVersion?: string;
    contentManifestSha256?: string;
    requiredSceneIds: number[];
    selectedSceneIds: number[];
    fallbackSceneIds: number[];
    plannedContentDurationSeconds?: number;
    probedDurationSeconds?: number;
    selections: FinalContentSelectionAudit[];
  };
};

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const payloadOf = (event: EchoflicksSessionEvent): JsonRecord => {
  const raw = isRecord(event.raw_payload) ? event.raw_payload : {};
  return isRecord(raw.payload) ? raw.payload : raw;
};

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const integer = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isInteger(value) ? value : undefined;

const numeric = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const decimalText = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0
    ? value
    : typeof value === "number" && Number.isFinite(value)
      ? String(value)
      : undefined;

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const integers = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter((item): item is number => Number.isInteger(item))
    : [];

const finalContentSelectionOf = (
  value: unknown,
): FinalContentSelectionAudit | undefined => {
  if (!isRecord(value)) return undefined;
  return {
    sceneId: integer(value.scene_id),
    assetLocatorSha256: text(value.asset_locator_sha256),
    assetProvenance: text(value.asset_provenance),
    providerId: text(value.provider_id),
    fallbackReason: text(value.fallback_reason),
    continuityBridgeId: text(value.continuity_bridge_id),
    continuityLegId: text(value.continuity_leg_id),
    continuityDisposition: text(value.continuity_disposition),
  };
};

const personSafetyOf = (
  value: unknown,
): FusionPersonSafetyAudit | undefined => {
  if (!isRecord(value)) return undefined;
  return {
    state: text(value.state),
    policyVersion: text(value.policy_version),
    detectorVersions: strings(value.detector_versions),
    sampledFrameCount: integer(value.sampled_frame_count),
    positiveFrameCount: integer(value.positive_frame_count),
    maxScore: numeric(value.max_score),
    primaryConfidence: numeric(value.primary_confidence),
    reasonCode: text(value.reason_code),
  };
};

const endFrameReceiptOf = (
  payload: JsonRecord,
): ContinuityAttemptAudit["endFrame"] => {
  const summary = isRecord(payload.i2v_prompt_audit_summary)
    ? payload.i2v_prompt_audit_summary
    : payload;
  const references = isRecord(summary.reference_frames)
    ? summary.reference_frames
    : null;
  if (
    !references ||
    references.schema_version !== "i2v-reference-frame-receipt.v1"
  ) {
    return undefined;
  }
  const endFrame = isRecord(references.end_frame) ? references.end_frame : null;
  return {
    requested: references.has_end_frame === true,
    applied: references.end_frame_applied === true,
    hashAgreement:
      typeof endFrame?.hash_agreement === "boolean"
        ? endFrame.hash_agreement
        : undefined,
    role: text(endFrame?.role),
  };
};

const seamOf = (value: unknown): ContinuitySeamAudit | null => {
  if (!isRecord(value)) return null;
  const fromScene = integer(value.from_scene_id);
  const toScene = integer(value.to_scene_id);
  const seamId = text(value.seam_id);
  const strategy = text(value.strategy);
  const execution = text(value.execution);
  if (
    fromScene === undefined ||
    toScene === undefined ||
    !seamId ||
    !strategy ||
    !execution
  ) {
    return null;
  }
  return {
    seamId,
    boundary: `${fromScene} → ${toScene}`,
    strategy,
    execution,
    confidence: numeric(value.confidence),
    identityRisk: text(value.identity_risk),
    reasons: strings(value.reasons),
  };
};

/**
 * Build the CRM audit view from an explicit allowlist. Media locations, prompts,
 * hashes, detector outputs, and provider payloads never enter the returned model.
 */
export const continuityAuditFromEvents = (
  events: EchoflicksSessionEvent[],
): ContinuityAudit | null => {
  const audit: ContinuityAudit = {
    degradations: [],
    attempts: [],
    qc: [],
  };
  let found = false;
  const degradations = new Map<
    string,
    ContinuityAudit["degradations"][number]
  >();
  const attempts = new Map<string, ContinuityAttemptAudit>();
  const qc = new Map<string, ContinuityQcAudit>();
  const ordered = events
    .map((event, index) => ({ event, index }))
    .sort((left, right) => {
      const leftTime = Date.parse(left.event.occurred_at ?? "");
      const rightTime = Date.parse(right.event.occurred_at ?? "");
      if (
        Number.isFinite(leftTime) &&
        Number.isFinite(rightTime) &&
        leftTime !== rightTime
      ) {
        return leftTime - rightTime;
      }
      return left.index - right.index;
    });

  for (const { event } of ordered) {
    const payload = payloadOf(event);
    if (event.event_type === "continuity.flag_evaluated") {
      found = true;
      audit.evaluated = {
        enabled: payload.enabled === true,
        productFlow: text(payload.product_flow),
        reason: text(payload.reason),
        configReleaseId: text(payload.config_release_id),
        configDigest: text(payload.config_digest),
      };
    } else if (event.event_type === "continuity.plan_created") {
      found = true;
      const plan = isRecord(payload.plan) ? payload.plan : payload;
      audit.plan = {
        planId: text(plan.plan_id),
        policyVersion: text(plan.policy_version),
        seams: Array.isArray(plan.seams)
          ? plan.seams
              .map(seamOf)
              .filter((seam): seam is ContinuitySeamAudit => seam !== null)
          : [],
      };
      const fusion = isRecord(plan.fusion) ? plan.fusion : null;
      const bridgeId = fusion ? text(fusion.bridge_id) : undefined;
      if (fusion && bridgeId) {
        audit.fusion = {
          bridgeId,
          seamId: text(fusion.seam_id),
          strategy: text(fusion.strategy),
          outgoingLegId: text(fusion.outgoing_leg_id),
          incomingLegId: text(fusion.incoming_leg_id),
          joinMethod: text(fusion.join_method),
          qcIssueCodes: [],
        };
      }
    } else if (event.event_type === "continuity.plan_validated") {
      found = true;
      audit.validation = {
        valid: payload.valid === true,
        reason: text(payload.reason),
        planId: text(payload.plan_id),
      };
    } else if (event.event_type === "continuity.execution_degraded") {
      found = true;
      const degradation = {
        seamId: text(payload.seam_id),
        sceneId: integer(payload.scene_id),
        reason: text(payload.reason),
        fallback: text(payload.fallback),
      };
      const key = [
        degradation.seamId,
        degradation.sceneId,
        degradation.reason,
        degradation.fallback,
      ].join(":");
      degradations.set(key, degradation);
    } else if (
      event.event_type === "scene.i2v.attempt_submitted" ||
      event.event_type === "scene.i2v.attempt_finished"
    ) {
      const seamId = text(payload.continuity_seam_id);
      if (!seamId) continue;
      found = true;
      const attempt = integer(payload.attempt);
      const attemptId =
        text(payload.audit_id) ?? `${seamId}:${attempt ?? "unknown"}`;
      attempts.set(attemptId, {
        attemptId,
        planId: text(payload.continuity_plan_id),
        seamId,
        sceneId: integer(payload.scene_id),
        attempt,
        status: text(payload.status),
        provider: text(payload.provider),
        concreteProvider: text(payload.concrete_provider),
        model: text(payload.model),
        elapsedMs: integer(payload.elapsed_ms),
        fallback: payload.fallback === true,
        errorCode: text(payload.error_code),
        legId: text(payload.continuity_leg_id),
        legDirection: text(payload.continuity_leg_direction),
        endFrame: endFrameReceiptOf(payload),
      });
    } else if (event.event_type === "continuity.qc.observed") {
      const seamId = text(payload.seam_id);
      if (!seamId) continue;
      found = true;
      const attempt = integer(payload.attempt);
      qc.set(`${seamId}:${attempt ?? "unknown"}`, {
        seamId,
        sceneId: integer(payload.scene_id),
        attempt,
        provider: text(payload.provider),
        disposition: text(payload.disposition),
        issueCodes: strings(payload.issue_codes),
        recommendedFallback: text(payload.recommended_fallback),
      });
    } else if (event.event_type === "continuity.costs.reported") {
      found = true;
      audit.cost = {
        estimatedCostUsd: decimalText(payload.estimated_cost_usd),
        providerReportedCostUsd: decimalText(
          payload.provider_reported_cost_usd,
        ),
        attemptCount: integer(payload.attempt_count) ?? 0,
        billableAttemptCount: integer(payload.billable_attempt_count) ?? 0,
        fallbackAttemptCount: integer(payload.fallback_attempt_count) ?? 0,
        qcRejectedAttemptCount: integer(payload.qc_rejected_attempt_count) ?? 0,
      };
    } else if (event.event_type === "continuity.fusion.candidates_validated") {
      const bridgeId = text(payload.bridge_id);
      if (!bridgeId) continue;
      found = true;
      audit.fusion = {
        bridgeId,
        seamId: text(payload.seam_id) ?? audit.fusion?.seamId,
        strategy: audit.fusion?.strategy,
        outgoingLegId: audit.fusion?.outgoingLegId,
        incomingLegId: audit.fusion?.incomingLegId,
        joinMethod: audit.fusion?.joinMethod,
        candidateCount: integer(payload.candidate_count),
        acceptedCandidateCount: integer(payload.accepted_count),
        executionStatus: audit.fusion?.executionStatus,
        qcDisposition: audit.fusion?.qcDisposition,
        qcIssueCodes: audit.fusion?.qcIssueCodes ?? [],
        erasureStatus: audit.fusion?.erasureStatus,
        deletedObjectCount: audit.fusion?.deletedObjectCount,
        committed: audit.fusion?.committed,
        fallbackRegenerated: audit.fusion?.fallbackRegenerated,
        personSafety: audit.fusion?.personSafety,
      };
    } else if (event.event_type === "continuity.fusion.bridge_executed") {
      const bridgeId = text(payload.bridge_id);
      if (!bridgeId) continue;
      found = true;
      audit.fusion = {
        bridgeId,
        seamId: text(payload.seam_id) ?? audit.fusion?.seamId,
        strategy: audit.fusion?.strategy,
        outgoingLegId: audit.fusion?.outgoingLegId,
        incomingLegId: audit.fusion?.incomingLegId,
        joinMethod: audit.fusion?.joinMethod,
        candidateCount: audit.fusion?.candidateCount,
        acceptedCandidateCount: audit.fusion?.acceptedCandidateCount,
        executionStatus:
          payload.committed === true
            ? "committed"
            : payload.fallback_regenerated === true
              ? "ordinary_fallback_regenerated"
              : "rejected",
        qcDisposition: audit.fusion?.qcDisposition,
        qcIssueCodes: audit.fusion?.qcIssueCodes ?? [],
        erasureStatus: audit.fusion?.erasureStatus,
        deletedObjectCount: audit.fusion?.deletedObjectCount,
        committed: payload.committed === true,
        fallbackRegenerated: payload.fallback_regenerated === true,
        personSafety: audit.fusion?.personSafety,
      };
    } else if (event.event_type === "continuity.fusion.qc_enforced") {
      const bridgeId = text(payload.bridge_id);
      if (!bridgeId) continue;
      found = true;
      audit.fusion = {
        bridgeId,
        seamId: text(payload.seam_id) ?? audit.fusion?.seamId,
        strategy: audit.fusion?.strategy,
        outgoingLegId: audit.fusion?.outgoingLegId,
        incomingLegId: audit.fusion?.incomingLegId,
        joinMethod: audit.fusion?.joinMethod,
        candidateCount: audit.fusion?.candidateCount,
        acceptedCandidateCount: audit.fusion?.acceptedCandidateCount,
        executionStatus: audit.fusion?.executionStatus,
        qcDisposition: text(payload.disposition),
        qcIssueCodes: strings(payload.issue_codes),
        erasureStatus: audit.fusion?.erasureStatus,
        deletedObjectCount: audit.fusion?.deletedObjectCount,
        committed: audit.fusion?.committed,
        fallbackRegenerated: payload.fallback_regenerated === true,
        personSafety: isRecord(payload.person_safety)
          ? {
              outgoing: personSafetyOf(payload.person_safety.outgoing),
              incoming: personSafetyOf(payload.person_safety.incoming),
            }
          : audit.fusion?.personSafety,
      };
    } else if (event.event_type === "continuity.fusion.erasure_completed") {
      found = true;
      if (audit.fusion) {
        audit.fusion = {
          ...audit.fusion,
          erasureStatus:
            payload.complete === true ? "complete" : text(payload.status),
          deletedObjectCount:
            integer(payload.deleted_count) ??
            integer(payload.deleted_object_count),
        };
      }
    } else if (event.event_type === "continuity.config_readback") {
      found = true;
      audit.configReadback = {
        desiredReleaseId: text(payload.desired_release_id),
        publishedReleaseId: text(payload.published_release_id),
        appliedReleaseId: text(payload.applied_release_id),
        frozenReleaseId: text(payload.frozen_release_id),
        desiredDigest: text(payload.desired_digest),
        publishedDigest: text(payload.published_digest),
        appliedDigest: text(payload.applied_digest),
        frozenDigest: text(payload.frozen_digest),
        service: text(payload.service),
        serviceReleaseSha: text(payload.service_release_sha),
        validationResult: text(payload.validation_result),
      };
    } else if (event.event_type === "final_video.created") {
      const manifest = isRecord(payload.content_manifest)
        ? payload.content_manifest
        : payload;
      if (manifest.schema_version !== "final-content.v1") continue;
      found = true;
      audit.finalContent = {
        schemaVersion: text(manifest.schema_version),
        contentManifestSha256: text(payload.content_manifest_sha256),
        requiredSceneIds: integers(manifest.required_scene_ids),
        selectedSceneIds: integers(manifest.selected_scene_ids),
        fallbackSceneIds: integers(manifest.fallback_scene_ids),
        plannedContentDurationSeconds: numeric(
          manifest.planned_content_duration_seconds,
        ),
        probedDurationSeconds: numeric(payload.probed_duration_seconds),
        selections: Array.isArray(manifest.selections)
          ? manifest.selections
              .map(finalContentSelectionOf)
              .filter((item): item is FinalContentSelectionAudit =>
                Boolean(item),
              )
          : [],
      };
    }
  }

  audit.degradations = [...degradations.values()];
  audit.attempts = [...attempts.values()];
  audit.qc = [...qc.values()];

  return found ? audit : null;
};
